<template>
    <b-modal id="contractor-excel-download-modal"
             hide-footer
             size="xl"
             :no-close-on-backdrop="downloadLoading"
             :no-close-on-esc="downloadLoading"
             @hide="closeModal"
    >
        <template #modal-title>
            <div class="d-flex align-items-center" style="gap: 8px;">
                {{ $t('참여기업 엑셀 다운로드') }}
            </div>
        </template>

        <b-overlay :show="downloadLoading"
                   rounded="sm"
                   opacity="0.9"
                   bg-color="#efefef">
            <section class="mb-4">
                <b-form @submit.prevent="searchData">
                    <b-card no-body bg-variant="white">
                        <template #header>
                            <div class="d-flex align-items-center justify-content-between">
                                <h5 class="mb-0">{{ $t('참여 프로젝트 검색') }}</h5>
                                <div v-show="downloadLoading">
                                    <div class="d-flex align-items-center">
                                        <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                    </div>
                                </div>
                                <div v-show="!downloadLoading">
                                    <b-btn
                                        variant="primary"
                                        :disabled="searchLoading"
                                        type="submit"
                                    >
                                        {{ $t('검색하기') }}
                                    </b-btn>
                                </div>
                            </div>
                        </template>
                        <div class="p-3">
                            <b-row>
                                <b-col cols="8" class="mb-3 mb-xl-0">
                                    <b-form-group
                                        :label="$t('프로젝트 코드')"
                                        label-for="input-project_code"
                                        class="mb-0"
                                    >
                                        <b-form-input id="input-project_code"
                                                      v-model="searchFormValue.project_code"
                                                      :placeholder="$t('검색할 프로젝트 코드를 입력해주세요.')"
                                                      :disabled="downloadLoading"
                                                      trim
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="4">
                                    <b-form-group
                                        :label="$t('분기')"
                                        label-for="input-period"
                                        class="mb-0"
                                    >
                                        <b-form-select id="input-period"
                                                       v-model="searchFormValue.period"
                                                       :disabled="downloadLoading"
                                        >
                                            <b-form-select-option :value="null">{{ $t('분기 선택') }}</b-form-select-option>
                                            <b-form-select-option v-for="period in periodList"
                                                                  :key="period.value"
                                                                  :value="period.value"
                                            >
                                                {{ period.text }}
                                            </b-form-select-option>
                                        </b-form-select>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-card>
                </b-form>
            </section>

            <section>
                <div class="d-flex align-items-center justify-content-between mb-2">
                    <div class="list-info">
                        <div>{{ $t('전체 개수') }}: {{ total_num }}</div>
                        <span>/</span>
                        <div>{{ $t('선택된 프로젝트 개수') }}: {{ selected_num }}</div>
                    </div>
                    <div>
                        <b-button size="sm" variant="outline-dark"
                                  :disabled="downloadLoading"
                                  class="mr-2"
                                  @click="excelDownload('selected')"
                        >
                            {{ $t('엑셀 선택 다운로드') }}
                        </b-button>

                        <b-button size="sm"
                                  :disabled="downloadLoading"
                                  @click="excelDownload('all')"
                        >
                            {{ $t('전체 엑셀 다운로드') }}
                        </b-button>
                    </div>
                </div>
                <b-overlay :show="searchLoading"
                           rounded="sm"
                           opacity="0.8"
                           bg-color="#efefef">
                    <b-card no-body>

                        <div class="virtual-table-wrapper">
                            <div class="header-container">
                                <table class="table b-table table-hover">
                                    <thead class="thead-light">
                                    <tr>
                                        <th v-for="(field, index) in fields"
                                            :key="field.key"
                                            :style="index === fields.length - 1 ? getLastThStyle(field) : getColumnStyle(field)"
                                            :class="getThClass(field)"
                                        >
                                            <template v-if="field.key === 'selected'">
                                                <b-form-checkbox
                                                    v-model="selectedAll"
                                                    :indeterminate="indeterminate"
                                                    @change="toggleSelectAll"
                                                />

                                            </template>
                                            <template v-else>
                                                {{ $t(field.label) }}
                                            </template>
                                        </th>
                                    </tr>
                                    </thead>
                                </table>
                            </div>
                            <div v-if="listItem.length === 0" class="empty-container">
                                <h6 class="font-italic mb-0">{{ $t('검색된 프로젝트가 없습니다.') }}</h6>
                            </div>
                            <div v-else ref="bodyScrollContainer" class="body-scroll-container">
                                <RecycleScroller
                                    v-slot="{ item }"
                                    :items="listItem"
                                    :item-size="48.6"
                                    key-field="index"
                                >
                                    <table class="body-table">
                                        <tbody>
                                        <tr class="body-row">
                                            <td v-for="field in fields"
                                                :key="field.key"
                                                :style="getColumnStyle(field)"
                                                :class="getTdClass(field)"
                                            >
                                                <template v-if="field.key === 'selected'">
                                                    <b-form-checkbox
                                                        :checked="mapSelectedItem[item.project_code].selected"
                                                        @change="toggleSelect(item)"
                                                    />
                                                </template>
                                                <template v-else-if="field.key === 'excel_download'">
                                                    <b-button size="sm" :disabled="downloadLoading"
                                                              variant="dark-border"
                                                              @click="excelDownload('single',item)"
                                                    >{{ $t('다운로드') }}
                                                    </b-button>
                                                </template>
                                                <template v-else-if="field.key === 'project_code'">
                                                    <span v-b-tooltip.hover="get_tooltip_title(item[field.key])">{{
                                                            item[field.key]
                                                        }}</span>
                                                </template>
                                                <template v-else-if="field.key === 'campaign_name'">
                                                    <span v-b-tooltip.hover="get_tooltip_title(item[field.key])">{{
                                                            item[field.key]
                                                        }}</span>
                                                </template>
                                                <template v-else-if="field.key === 'client'">
                                                    <span v-b-tooltip.hover="get_tooltip_title(item[field.key])">{{
                                                            item[field.key]
                                                        }}</span>
                                                </template>
                                                <template v-else>
                                                    {{ item[field.key] }}
                                                </template>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </RecycleScroller>
                            </div>
                        </div>
                    </b-card>
                </b-overlay>

            </section>

            <template #overlay>
                <div class="text-center">
                    <b-spinner size="small"></b-spinner>
                    <div class="mt-2">
                        {{ $t('요청하신 내용으로 엑셀을 다운로드 중입니다.') }}
                    </div>
                    <div>
                        {{ $t('잠시 기다려주세요...') }}
                    </div>
                </div>

            </template>
        </b-overlay>


    </b-modal>
</template>

<script>
import {mapGetters} from 'vuex';
import {columns} from '@shared/components/contractor/components/ContractorExcelDownload/data';
import FuzzySearch from 'fuzzy-search';
import dayjs from 'dayjs';
import downloadFile from '@shared/mixin/downloadFile';

export default {
    name: 'ContractorExcelDownloadModal',
    mixins: [
        downloadFile
    ],
    data() {
        return {
            fields: columns,
            searchFormValue: {
                project_code: '',
                period: null,
                temp_period: undefined,
            },
            listItem: [],
            mapSelectedItem: {},
            selected_num: 0,
            searchLoading: false,
            downloadLoading: false,
            periodObj: {},
            selectedAll: false,
            scrollbarWidth: 0,
            searcher: null
        };
    },
    computed: {
        ...mapGetters('contractor', ['Get_Contractor', 'GET_ProjectList']),
        periodList() {
            const keys = Object.keys(this.periodObj);
            keys.sort((a, b) => {
                const aParts = a.split('_');
                const bParts = b.split('_');
                if (aParts[0] === bParts[0]) {
                    return parseInt(parseInt(bParts[1] - aParts[1]));
                }
                return parseInt(parseInt(bParts[0] - aParts[0]));
            });
            return Object.keys(this.periodObj).map(key => {
                return {
                    value: key,
                    text: this.$t(key)
                };
            });
        },
        indeterminate() {
            return this.selected_num > 0 && this.selected_num < this.listItem.length;
        },
        get_tooltip_title() {
            return (data) => {
                return data ? {title: data, placement: 'topright'} : null;
            };
        },
        total_num() {
            return this.GET_ProjectList.length;
        },
    },
    watch: {
        GET_ProjectList: {
            handler() {
                this.updateFuzzySearch();
            }
        }
    },
    mounted() {
        this.$root.$on('bv::tooltip::show', this.onTooltipShow);
    },
    beforeDestroy() {
        this.$root.$off('bv::tooltip::show', this.onTooltipShow);
    },
    methods: {
        updateFuzzySearch() {
            const periodObj = {};
            const mapSelectedItem = {};
            this.GET_ProjectList.forEach(item => {
                periodObj[item.period] = true;
                mapSelectedItem[item.project_code] = item;
                mapSelectedItem[item.project_code].selected = false;
            });
            this.periodObj = periodObj;
            this.mapSelectedItem = mapSelectedItem;

            this.$nextTick(() => {
                this.searchData();
            });
        },
        closeModal(event) {
            if (this.downloadLoading) {
                event.preventDefault();
                return;
            }
            this.$bvModal.hide('contractor-excel-download-modal');
        },
        waitTime(time) {
            return new Promise(resolve => {
                setTimeout(() => resolve(), time);
            });
        },
        async searchData() {
            try {
                this.searchLoading = true;
                await this.waitTime(300);
                if (this.searchFormValue.period !== this.searchFormValue.temp_period) {
                    this.searchFormValue.temp_period = this.searchFormValue.period;
                    const filteredList = this.GET_ProjectList.filter(item => {
                        if (this.searchFormValue.period === null) return true;
                        return item.period === this.searchFormValue.period;
                    });
                    this.searcher = new FuzzySearch(filteredList, ['project_code'], {
                        caseSensitive: false,
                    });
                }
                const tempResult = this.searcher.search(this.searchFormValue.project_code);
                this.listItem = tempResult.map((item, index) => {
                    return {
                        ...item,
                        index: tempResult.length - index,
                        selected: false,
                        target_end_date: item.target_end_date ? item.target_end_date.substring(0, 10) : '',
                    };
                });
            } catch (e) {

            } finally {
                this.searchLoading = false;
            }

        },
        toggleSelectAll() {
            const mapSelectedItem = JSON.parse(JSON.stringify(this.mapSelectedItem));
            this.listItem.forEach(item => {
                if (this.mapSelectedItem[item.project_code].selected !== this.selectedAll) {
                    this.selected_num += this.selectedAll ? 1 : -1;
                }
                mapSelectedItem[item.project_code].selected = this.selectedAll;
            });
            this.mapSelectedItem = mapSelectedItem;
        },
        toggleSelect(item) {
            if (this.mapSelectedItem[item.project_code].selected) {
                this.selected_num--;
            } else {
                this.selected_num++;
            }
            this.mapSelectedItem[item.project_code].selected = !this.mapSelectedItem[item.project_code].selected;
        },
        getLastThStyle(field) {
            const baseStyle = this.getColumnStyle(field);
            if (this.scrollbarWidth > 0 && baseStyle.width) {
                baseStyle.width = `calc(${baseStyle.width} + ${this.scrollbarWidth}px)`;
            } else if (this.scrollbarWidth > 0 && !baseStyle.width) {
                baseStyle.width = `${this.scrollbarWidth}px`;
            }
            return baseStyle;
        },
        getColumnStyle(field) {
            const style = {};
            if (field.width) {
                style.width = typeof field.width === 'number' ? `${field.width}px` : field.width;
            }
            if (field.hAlign) style.textAlign = field.hAlign;
            if (field.vAlign) style.verticalAlign = field.vAlign === 'center' ? 'middle' : field.vAlign;
            return style;
        },
        getThClass(field) {
            return field.thClass || '';
        },
        getTdClass(field) {
            return field.tdClass || '';
        },
        async excelDownload(type, item) {
            let project_codes = [];

            try {
                this.downloadLoading = true;
                if (type === 'single' && item) {
                    project_codes.push(item.project_code);
                } else if (type === 'selected' && this.selected_num > 0) {
                    project_codes = Object.values(this.mapSelectedItem).filter(item => item.selected).map(item => item.project_code);
                } else if (type === 'all') {
                    project_codes = this.GET_ProjectList.map(item => item.project_code);
                }
                if (project_codes.length === 0) {
                    this.$showModal({
                        type: 'alert',
                        state: 'warning',
                        title: this.$t('엑셀 다운로드 에러'),
                        message: this.$t('다운로드할 항목이 없습니다.'),
                    });
                    return;
                }

                const _res_participants_excel = await this.$axios({
                    method: 'post',
                    url: '/projects/export-excel',
                    responseType: 'blob',
                    data: {
                        project_codes: project_codes,
                    }
                });
                this.downloadFileFromResponse(_res_participants_excel, `${this.$t('참여기업_진행현황_데이터')}_${dayjs().format('YYYY_MM_DD_HH:mm')}`);
                this.$EventBus.$emit('toast-success', this.$t('엑셀 다운로드'), this.$t('엑셀 다운로드를 완료했습니다..'));

            } catch (e) {
                const h = this.$createElement;

                console.error('Error downloading Excel:', e);
                console.dir(e);
                this.$showModal({
                    type: 'alert',
                    state: 'error',
                    title: this.$t('엑셀 다운로드 에러'),
                    message: h('div', {}, [
                        this.$t('엑셀 다운로드 중 오류가 발생했습니다.'),
                        this.$t('지속적으로 문제 발생 시, 관리자에게 문의해주세요.'),
                    ]),
                });

            } finally {
                this.downloadLoading = false;
            }
        },
        onTooltipShow(bvEvent) {
            const targetEl = bvEvent.target;

            const tooltip = bvEvent.vueTarget;
            if (!tooltip) return;

            const width = targetEl.offsetWidth;
            tooltip.offset = -(width / 2);

        },


    },
};
</script>

<style lang="scss" scoped>
.empty-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50vh;
    padding: 2rem;
    color: #6c757d;
    font-style: italic;
}

.list-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    color: #6c757d;
}
</style>
